// Turning a Firestore write into something that survives a tab closing, and
// back again.
//
// The queue's unit of work is a **materialised value**, never a closure to
// re-invoke — `core/src/queue.rs` opens with that rule and gives the reason:
// `useExpenses.addCategory` mints `freeId('ecat')` and *then* builds its batch,
// so a queue that retried by re-running the mutator would append a second cash
// payment on `ledgerEntries`. Materialised means serialisable, and this file is
// where a `{ name: 'Piano', updatedAt: serverTimestamp() }` becomes rows that
// can sit in IndexedDB overnight and come back as the same write.
//
// The shape it becomes is the core's, not one of ours: `QueueField` is
// `{ opId, field, value, transform }`, with `value` **opaque** — "whatever the
// platform serialised; nothing here reads it". This file is the platform that
// serialises it.
//
// ── The closed whitelist, and why it is a whitelist ────────────────────────
//
// A value is encodable if it is JSON and nothing else: a string, a finite
// number, a boolean, null, a plain object, or an array of those. Everything
// else is refused by name at enqueue.
//
// A blacklist over `JSON.stringify` fails silently: a `Date` comes back a
// *string*, `NaN` and `Infinity` become `null`, and an `undefined` property —
// which Firestore throws on — simply vanishes. Each is a corrupted document
// with no error. A whitelist stops the write at enqueue, before anything
// durable happens, with the field named.
//
// ── Sentinels: the queue carries them, it does not resolve them ────────────
//
// #125's D6: stamping a device clock at enqueue would write a possibly-skewed
// time into every `serverTimestamp()` field, and `balanceAsOf` and
// `entriesBetween` sort on dates — a phone with a bad clock would reorder a
// family's ledger. So
// a `serverTimestamp()` crosses the queue as the string `"serverTimestamp"` in
// the `transform` column with an empty `value`, and becomes a real sentinel
// again only at the moment of dispatch, however many days later.
//
// ── Why the SDK's own sentinels are recognised here ────────────────────────
//
// So a call site can keep the SDK's import (#126): `serverTimestamp()` and
// `deleteField()` carry no payload, so an instance of the SDK's exported
// `FieldValue` class is fully reconstructible from its `_methodName`.
//
// `_methodName` is an SDK internal and this file is the only place that
// depends on it. It is assigned in `FieldValue`'s constructor and read back in
// the SDK's own error messages, so it survives minification;
// `test/ui/opCodec.test.jsx` asserts it directly, so an SDK that renames it
// fails a test rather than dropping timestamps.
//
// ── What the whitelist costs (#210) ───────────────────────────────────────
//
// A Firestore `Timestamp` is not JSON, so the whitelist refuses one — and a
// value read back off a stored document is exactly where one comes from.
// `students` and `families` guard `createdAt` in `firestore.rules` with
// `createdAtUnchanged()`, which accepts only the **stored** instant back: a
// fresh `serverTimestamp()` resolves to a different one and is refused, and
// omitting the field is refused too. So a whole-document `set` on such a
// collection cannot be expressed through this queue. It is unreachable:
//
//   * a *merging* set has no problem. `request.resource.data` is the merged
//     result, so the stored `createdAt` is carried in by construction — and
//     conflict class three prescribes per-field merge for exactly these
//     collections, so every student write in `src/` is a merge;
//   * `reconcile::rule_violations` returns `wholeDocumentSet` on those
//     collections to keep it that way.
//
// A typed `Timestamp` encoding would put a second instant format through the
// codec for a write nobody makes, and relaxing `createdAtUnchanged()` would
// loosen a server rule to suit a client's serialisation.
//
// **#232 narrows this.** `TRANSFORM.enqueuedAt` resolves
// `createdAt` once at enqueue and carries the instant in `value`, so a document
// *this device created* can be rewritten whole with the identical instant. A
// document created elsewhere still cannot: its `createdAt` is only available as
// the stored `Timestamp`, and that is what the whitelist refuses.
//
// `test/integration/queueConflictClasses.test.jsx` routes one write through
// the raw SDK (`sdkSetDoc`) because it demonstrates a **Firestore** semantic —
// a whole set replaces the document — that the facade cannot express.
//
// **The SDK's `arrayUnion` and `arrayRemove` are refused**: their elements
// live in a private field, so they cannot be reconstructed from the class. The
// codec says to import ours from `./writes` instead.
//
// **Ours carry `arrayRemove`, and `increment` is not carried** (#288).
// `increment` is refused *by the core*, in `core/src/queue.rs`'s `defects`,
// because a double-apply corrupts it and no id discipline can fix that.
// Removing an absent element is a no-op, so a replayed `arrayRemove` reaches
// the same document state.
//
// The `arrayRemove` callers write tag membership: `useTags.saveTag` and the
// tag detachment in `useStudents.deleteStudents`. Nothing calls `increment`.

import { FieldValue } from 'firebase/firestore';

/** The `transform` column's vocabulary. These strings are the core's. */
export const TRANSFORM = {
  none: '',
  serverTimestamp: 'serverTimestamp',
  arrayUnion: 'arrayUnion',
  arrayRemove: 'arrayRemove',
  increment: 'increment',
  deleteField: 'deleteField',
  /**
   * The client's time of intent, resolved once here and carried in `value`.
   *
   * **One field wide.** `core/src/queue.rs` refuses it anywhere but
   * `createdAt` — `MISPLACED_ENQUEUED_AT` — so the ledger argument above stays
   * true of every other sentinel field, which is why it is a separate
   * transform rather than an exception to `serverTimestamp`.
   *
   * It exists because a replay re-resolves a `serverTimestamp`, and
   * `createdAtUnchanged()` in firestore.rules pins that field: the second
   * delivery of a write that already landed is refused, classified permanent,
   * and dead-lettered (#232).
   */
  enqueuedAt: 'enqueuedAt',
};

/**
 * The one field the enqueue-resolved stamp may be written to.
 *
 * Duplicated from `ENQUEUED_AT_FIELD` in `core/src/queue.rs` rather than read
 * from it, because this module deliberately imports nothing — it is the half of
 * the queue that runs with no Firebase and no wasm. The core refuses a mismatch,
 * so a drift between the two is a defect rather than a silent widening, and
 * `test/ui/opCodec.test.jsx` asserts the pair agree.
 */
export const ENQUEUED_AT_FIELD = 'createdAt';

/** The `kind` column's vocabulary. Also the core's. */
export const KIND = {
  set: 'set',
  merge: 'merge',
  delete: 'delete',
};

/**
 * Our own sentinel.
 *
 * A class rather than a tagged plain object so that a payload containing one is
 * unambiguous: a plain object with a `transform` key is something a teacher
 * might genuinely be storing, and an `instanceof` cannot be spoofed by data.
 */
class CodaSentinel {
  constructor(transform, elements) {
    this.transform = transform;
    this.elements = elements;
    Object.freeze(this);
  }
}

/** `serverTimestamp()`, carried unresolved. Interchangeable with the SDK's. */
export const serverTimestamp = () => new CodaSentinel(TRANSFORM.serverTimestamp);

/** `deleteField()`, carried unresolved. Interchangeable with the SDK's. */
export const deleteField = () => new CodaSentinel(TRANSFORM.deleteField);

/**
 * `arrayUnion(...)`, carried unresolved.
 *
 * **Import this one rather than the SDK's**, which the codec refuses: the
 * elements being added are private to the SDK's instance, so a queued write
 * built from it could not be reconstructed after a reload.
 */
export const arrayUnion = (...elements) => new CodaSentinel(TRANSFORM.arrayUnion, elements);

/**
 * `arrayRemove(...)`, carried unresolved.
 *
 * **Import this one rather than the SDK's**, for the same reason as
 * `arrayUnion` above: the elements are private to the SDK's instance.
 *
 * `useTags.saveTag` (#198, #288) emits a union for the students a tag gained
 * and a removal for the ones it lost rather than writing the membership whole.
 * The two commute at Firestore, so two offline devices editing one tag both
 * keep their edit.
 */
export const arrayRemove = (...elements) => new CodaSentinel(TRANSFORM.arrayRemove, elements);

/** What the SDK's sentinels are called, and whether we can carry one. */
const SDK_SENTINELS = {
  serverTimestamp: TRANSFORM.serverTimestamp,
  deleteField: TRANSFORM.deleteField,
};

function isPlainObject(value) {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Is this value JSON, all the way down?
 *
 * Returns the offending sub-path, or null. A path rather than a boolean because
 * the refusal below has to name *which* part of a nested map was wrong, and
 * `attendance.notes` is a nested map this app really writes.
 */
function unencodableAt(value, at) {
  if (value === null) return null;
  const type = typeof value;
  if (type === 'string' || type === 'boolean') return null;
  if (type === 'number') return Number.isFinite(value) ? null : at;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const bad = unencodableAt(value[i], `${at}[${i}]`);
      if (bad) return bad;
    }
    return null;
  }
  if (isPlainObject(value)) {
    for (const key of Object.keys(value)) {
      const bad = unencodableAt(value[key], `${at}.${key}`);
      if (bad) return bad;
    }
    return null;
  }
  return at;
}

/** A refusal a person can act on: what was wrong, where, and what to do. */
function refuse(message) {
  const error = new Error(`[queue] ${message}`);
  error.code = 'coda/unencodable-write';
  return error;
}

/**
 * Read a value's transform, or null if it is not a sentinel at all.
 *
 * Throws for a sentinel we recognise and cannot carry, which is the honest
 * answer — "I know exactly what you meant and I will not pretend to store it".
 */
function transformOf(value, field) {
  if (value instanceof CodaSentinel) return value;
  if (value instanceof FieldValue) {
    const name = value._methodName;
    const carried = SDK_SENTINELS[name];
    if (carried) return new CodaSentinel(carried);
    throw refuse(
      `\`${field}\` uses the Firebase SDK's ${name || 'unknown'}(), which the offline queue `
      + 'cannot carry across a reload: its arguments are private to the SDK instance. '
      + `Import ${name || 'the sentinel'} from './writes' instead.`,
    );
  }
  return null;
}

/**
 * One document's fields, as the core's join table.
 *
 * `data` is exactly what a caller passed, and every key becomes one row with
 * its name **verbatim**.
 *
 * ── A dotted key is a field name here, not a path (#209, #254) ─────────────
 *
 * `OpField.field` is not a dotted path within the document. The queue has three
 * kinds — `set`, `merge`, `delete` — and no write in `src/` uses `updateDoc`
 * or `.update(`. `dispatch` in `./writes.js` turns each into
 * `batch.set(ref, data)` or `batch.set(ref, data, { merge: true })`, and the
 * SDK splits a dotted key on dots for `updateDoc` alone: a merging set of
 * `'scheduling.leadTimeHours'` produces a **literal top-level field containing
 * a dot** and leaves the `scheduling` map untouched. No call site passes a
 * dotted key.
 *
 * ── The read store relies on the same answer (#254) ────────────────────────
 *
 * `src/data/docShape.js` keys stored documents at the **top level because this
 * file does**, and `reconcile` pairs a remote field with a local one by exact
 * name; a dotted `contact.email` on one side would never meet a whole
 * `contact` on the other. A dotted key is carried through unchanged as one
 * field whose name contains a dot, which `test/ui/opCodec.test.jsx` asserts.
 */
export function encodeFields(opId, data, now = Date.now) {
  if (!isPlainObject(data)) {
    throw refuse(`a write to \`${opId}\` was given ${typeof data} rather than an object of fields`);
  }
  return Object.keys(data).map((field) => {
    const value = data[field];
    const sentinel = transformOf(value, field);
    if (sentinel) {
      // `createdAt` is resolved here, once, rather than at each dispatch (#232).
      // Every replay of this row then carries the identical instant, which is
      // what lets `createdAtUnchanged()` hold across a redelivery instead of
      // refusing it. Scoped to this one field — see TRANSFORM.enqueuedAt.
      if (sentinel.transform === TRANSFORM.serverTimestamp && field === ENQUEUED_AT_FIELD) {
        return {
          opId,
          field,
          value: JSON.stringify(now()),
          transform: TRANSFORM.enqueuedAt,
        };
      }
      return {
        opId,
        field,
        // `arrayUnion` and `arrayRemove` are the sentinels with a payload; the
        // other two carry an empty value, exactly as the core documents.
        value: sentinel.elements ? JSON.stringify(sentinel.elements) : '',
        transform: sentinel.transform,
      };
    }
    if (value === undefined) {
      throw refuse(
        `\`${field}\` is undefined. Firestore refuses an undefined field today, so this would `
        + 'have thrown at the call site; the queue refuses it here rather than silently '
        + 'dropping the field, which is what JSON.stringify would do.',
      );
    }
    const bad = unencodableAt(value, field);
    if (bad) {
      throw refuse(
        `\`${bad}\` is not JSON (it is ${describe(value, bad, field)}). A queued write has to `
        + 'survive a reload, so its fields must be strings, finite numbers, booleans, null, '
        + 'plain objects or arrays of those. Dates belong here as date-only strings — see '
        + 'CLAUDE.md.',
      );
    }
    return { opId, field, value: JSON.stringify(value), transform: TRANSFORM.none };
  });
}

function describe(value, bad, field) {
  const at = bad === field ? value : undefined;
  if (at === undefined) return 'a nested value of an unsupported type';
  if (at instanceof Date) return 'a Date';
  if (typeof at === 'number') return String(at);
  if (typeof at === 'function') return 'a function';
  return `a ${at?.constructor?.name || typeof at}`;
}

/**
 * The fields of one op, back as a payload the SDK will accept.
 *
 * `sentinels` maps a transform name onto the SDK's real constructor, and is
 * passed in rather than imported so that this module stays free of everything
 * except the `FieldValue` class it needs to recognise. The queue that calls
 * this is tested with no Firebase at all.
 */
export function decodeFields(fields, sentinels) {
  const out = {};
  for (const { field, value, transform } of fields) {
    if (transform === TRANSFORM.none) {
      out[field] = JSON.parse(value);
    } else {
      const make = sentinels[transform];
      if (!make) {
        // Reachable only if a record written by a *newer* build is replayed by
        // an older one. Named rather than ignored, so no field is silently
        // dropped.
        throw refuse(`a queued write carries an unknown transform \`${transform}\` on \`${field}\``);
      }
      // Three shapes: the two array transforms spread their stored elements,
      // the enqueue-resolved stamp passes its single stored instant, and the
      // payload-free sentinels take nothing.
      if (transform === TRANSFORM.arrayUnion || transform === TRANSFORM.arrayRemove) {
        out[field] = make(...JSON.parse(value));
      } else if (transform === TRANSFORM.enqueuedAt) out[field] = make(JSON.parse(value));
      else out[field] = make();
    }
  }
  return out;
}
